import { Box, Button, Stack, Typography, IconButton, Tooltip, formatDate } from "@compass/core";
import RefreshIcon from "@mui/icons-material/Refresh";
import Skeleton from "@mui/material/Skeleton";
import { useState } from "react";
import { DashboardCard } from "../components/DashboardCard";
import { Accordion, AccordionSummary, AccordionDetails } from "components/common/Accordion";
import { NotificationDataProvider, useNotificationData } from "../contexts/NotificationDataContext";

const PAGE_SIZE = 5;

const NotificationsCardContent = () => {
  const { notifications, loading, error, markAsRead, fetchNotifications } = useNotificationData();

  const [expandedId, setExpandedId] = useState<number | false>(false);
  const [page, setPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleAccordionChange = (notificationId: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedId(isExpanded ? notificationId : false);
    if (isExpanded) {
      markAsRead(notificationId);
    }
  };

  const handlePageChange = async (newPage: number) => {
    setPage(newPage);
    await fetchNotifications(newPage, PAGE_SIZE);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      setPage(1);
      await fetchNotifications(1, PAGE_SIZE);
    } finally {
      setIsRefreshing(false);
    }
  };

  const hasNextPage = notifications.length === PAGE_SIZE;

  if (loading && !isRefreshing) {
    return (
      <Stack direction="column" spacing="sm">
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={60} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" color="error">{error}</Typography>
      </Box>
    );
  }

  if (notifications.length === 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
        <Typography variant="body1" color="text.secondary">No notifications</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Tooltip title="Refresh Notifications">
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            sx={{
              animation: isRefreshing ? "spin 1s linear infinite" : "none",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Stack direction="column" spacing="sm" sx={{ flexGrow: 1, minHeight: 0 }}>
        {notifications.map((notification) => (
          <Accordion
            key={notification.id}
            expanded={expandedId === notification.id}
            onChange={handleAccordionChange(notification.id)}
          >
            <AccordionSummary>
              <Stack direction="row" spacing="sm" alignItems="center">
                {!notification.isRead && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      flexShrink: 0,
                    }}
                  />
                )}
                <Typography variant="body2" fontWeight={notification.isRead ? "normal" : "medium"}>
                  {formatDate(notification.createdDate)} - {notification.title}
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {notification.content}
              </Typography>

              {notification.isBug && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    bgcolor: "error.light",
                    color: "error.contrastText",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    mt: 1,
                    width: "fit-content",
                  }}
                >
                  <Typography variant="caption" fontWeight="medium">Bug Fix</Typography>
                </Box>
              )}

              {notification.userManualLink && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    href={notification.userManualLink}
                    rel="noopener noreferrer"
                  >
                    View User Manual
                  </Button>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, pt: 2 }}>
        <Button size="small" variant="outlined" disabled={page === 1} onClick={() => handlePageChange(page - 1)}>
          Previous
        </Button>
        <Typography variant="body2">Page {page}</Typography>
        <Button size="small" variant="outlined" disabled={!hasNextPage} onClick={() => handlePageChange(page + 1)}>
          Next
        </Button>
      </Box>
    </Box>
  );
};

export const NotificationsCard = () => (
  <NotificationDataProvider>
    <DashboardCard title="Notifications">
      <NotificationsCardContent />
    </DashboardCard>
  </NotificationDataProvider>
);